import { createContext, useContext, useState, useCallback, useRef } from 'react';

const ToastContext = createContext(null);

const TOAST_DURATION_MS = 3000;
const TOAST_DURATION_WITH_ACTION_MS = 4500;

const TOAST_ICONS = {
    success: '✓',
    error: '!',
    warning: '!',
    info: 'i',
};

export function ToastProvider({ children }) {
    const [toast, setToast] = useState(null);
    const timeoutRef = useRef(null);

    const clearToastTimeout = useCallback(() => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, []);

    const hideToast = useCallback(() => {
        clearToastTimeout();
        setToast(null);
    }, [clearToastTimeout]);

    const showToast = useCallback(({ message, subtitle, action, variant = 'success', duration } = {}) => {
        const resolvedVariant = TOAST_ICONS[variant] ? variant : 'success';
        clearToastTimeout();
        setToast({ message, subtitle, action, variant: resolvedVariant });
        const ms = duration ?? (action ? TOAST_DURATION_WITH_ACTION_MS : TOAST_DURATION_MS);
        timeoutRef.current = setTimeout(() => {
            setToast(null);
            timeoutRef.current = null;
        }, ms);
    }, [clearToastTimeout]);

    return (
        <ToastContext.Provider value={{ showToast, hideToast }}>
            {children}
            {toast && (
                <div
                    className={`toast-global toast-global--${toast.variant || 'success'}`}
                    role={toast.variant === 'error' ? 'alert' : 'status'}
                    aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
                >
                    <div className="toast-global__icon" aria-hidden>
                        {TOAST_ICONS[toast.variant] || TOAST_ICONS.success}
                    </div>
                    <div className="toast-global__body">
                        <div className="toast-global__title">{toast.message}</div>
                        {toast.subtitle && (
                            <div className="toast-global__subtitle">{toast.subtitle}</div>
                        )}
                    </div>
                    {toast.action && (
                        <button
                            type="button"
                            className="toast-global__action"
                            onClick={(e) => {
                                toast.action.onClick?.(e);
                                hideToast();
                            }}
                        >
                            {toast.action.label}
                        </button>
                    )}
                    <button
                        type="button"
                        className="toast-global__dismiss"
                        aria-label="Dismiss"
                        onClick={hideToast}
                    >
                        ×
                    </button>
                </div>
            )}
        </ToastContext.Provider>
    );
}

export function useToast() {
    const ctx = useContext(ToastContext);
    if (!ctx) return { showToast: () => {}, hideToast: () => {} };
    return ctx;
}
